import { createEmitter } from "../../Utilities/events";
import {
  CastingCapabilityState,
  CastingConnectionState,
} from "./CastingEvents";

/**
 * Detect Safari / iOS / macOS WebKit AirPlay surface.
 * Chrome / Windows / non-WebKit must report NOT SUPPORTED — never pretend.
 * @returns {{ supported: boolean, reason: string }}
 */
export function detectAirPlayEnvironment() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      supported: false,
      reason: "AirPlay requires a browser document (Safari / iOS / macOS WebKit).",
    };
  }
  var ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  var isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== "undefined" &&
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1);
  var isSafari =
    /Safari/.test(ua) &&
    !/Chrome|Chromium|Edg|OPR|Firefox|CriOS|FxiOS/.test(ua);
  var hasWebKitPicker =
    typeof HTMLVideoElement !== "undefined" &&
    typeof HTMLVideoElement.prototype.webkitShowPlaybackTargetPicker ===
      "function";

  if (hasWebKitPicker || isIOS || isSafari) {
    return {
      supported: true,
      reason: hasWebKitPicker
        ? "WebKit AirPlay API present"
        : "Safari / iOS WebKit environment — AirPlay picker binds after video attach",
    };
  }

  return {
    supported: false,
    reason:
      "AirPlay is not supported in this browser. Use Safari on macOS/iOS, or a future native iOS/macOS app.",
  };
}

/**
 * Apple AirPlay CastingProvider via HTMLMediaElement WebKit APIs.
 * Web-compatible path: Safari / iOS / macOS WebKit only.
 * Same abstraction can later be driven by a native iOS/macOS bridge.
 *
 * @param {object} [options]
 */
export function createAirPlayProvider(options) {
  options = options || {};
  var emitter = createEmitter();
  var state = CastingConnectionState.DISCONNECTED;
  var available = false;
  var video = null;
  var devices = [];
  var syncTimer = null;
  var lastError = null;
  var env = detectAirPlayEnvironment();
  var browserSupported = !!env.supported;

  function setState(next) {
    if (state === next) return;
    state = next;
    emitter.emit("connectionState", state);
  }

  function capabilities() {
    return {
      id: "airplay",
      name: "AirPlay",
      discover: browserSupported,
      play: browserSupported,
      pause: browserSupported,
      seek: browserSupported,
      stop: browserSupported,
      volume: browserSupported,
      mute: browserSupported,
      playbackRate: browserSupported,
      textTracks: browserSupported,
      audioTracks: browserSupported,
      reconnect: false,
      operationalOnWeb: browserSupported,
      requiresWebKit: true,
      note: browserSupported
        ? "Playback remotes through the media element; picker is system UI"
        : env.reason,
      environmentReason: env.reason,
    };
  }

  function capabilityState() {
    if (!browserSupported) {
      return CastingCapabilityState.UNSUPPORTED;
    }
    if (state === CastingConnectionState.CONNECTING) {
      return CastingCapabilityState.CONNECTING;
    }
    if (isWireless()) {
      if (video && !video.paused && !video.ended) {
        return CastingCapabilityState.PLAYING;
      }
      if (video && video.paused) {
        return CastingCapabilityState.PAUSED;
      }
      return CastingCapabilityState.CONNECTED;
    }
    if (available) {
      return CastingCapabilityState.AVAILABLE;
    }
    return CastingCapabilityState.DISCONNECTED;
  }

  function onAvailability(e) {
    available = !!(e && (e.availability === "available" || e.availability === true));
    // Some WebKit builds use string "available" / "not-available"
    if (e && typeof e.availability === "string") {
      available = e.availability === "available";
    }
    emitter.emit("availability", available);
    refreshDevices();
  }

  function refreshDevices() {
    // Only list when WebKit reports real availability or an active wireless target.
    // Never invent AirPlay devices on unsupported browsers.
    if (!browserSupported) {
      devices = [];
    } else if (isWireless()) {
      devices = [
        {
          id: "airplay-active",
          name: "AirPlay",
          provider: "airplay",
          type: "airplay",
          connected: true,
          discoveryMode: "system_picker",
        },
      ];
    } else if (available) {
      devices = [
        {
          id: "airplay-picker",
          name: "AirPlay (system picker)",
          provider: "airplay",
          type: "airplay",
          connected: false,
          discoveryMode: "system_picker",
        },
      ];
    } else {
      devices = [];
    }
    emitter.emit("devices", devices.slice());
  }

  function isWireless() {
    try {
      return !!(video && video.webkitCurrentPlaybackTargetIsWireless);
    } catch (e) {
      return false;
    }
  }

  function pollConnection() {
    var wireless = isWireless();
    if (wireless && state !== CastingConnectionState.CONNECTED) {
      setState(CastingConnectionState.CONNECTED);
      emitter.emit("session", { type: "started" });
      startSync();
    } else if (!wireless && state === CastingConnectionState.CONNECTED) {
      setState(CastingConnectionState.DISCONNECTED);
      stopSync();
      emitter.emit("session", { type: "ended", reason: "wireless-lost" });
    }
    refreshDevices();
  }

  function startSync() {
    stopSync();
    syncTimer = setInterval(function () {
      pollConnection();
      emitter.emit("remoteState", getRemoteState());
    }, 1000);
  }

  function stopSync() {
    if (syncTimer) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  }

  function getRemoteState() {
    if (!video) {
      return {
        target: "local",
        playing: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        muted: false,
        playbackRate: 1,
      };
    }
    var remote = isWireless();
    return {
      target: remote ? "remote" : "local",
      playing: !video.paused && !video.ended,
      currentTime: video.currentTime || 0,
      duration: video.duration || 0,
      volume: video.volume,
      muted: video.muted,
      playbackRate: video.playbackRate || 1,
    };
  }

  return {
    name: "airplay",
    getCapabilities: capabilities,
    /**
     * Bind the engine's video element for AirPlay routing.
     * @param {HTMLVideoElement} el
     */
    attachMediaElement: function (el) {
      if (video) {
        try {
          video.removeEventListener(
            "webkitplaybacktargetavailabilitychanged",
            onAvailability
          );
        } catch (e) {
          /* ignore */
        }
      }
      video = el;
      if (!video) return;
      try {
        video.addEventListener(
          "webkitplaybacktargetavailabilitychanged",
          onAvailability
        );
        // Probe: if API missing, stay unavailable
        if (typeof video.webkitShowPlaybackTargetPicker !== "function") {
          available = false;
        }
      } catch (e) {
        available = false;
        lastError = e;
      }
      refreshDevices();
    },
    initialize: function () {
      available = false;
      if (!browserSupported) {
        emitter.emit("availability", false);
        emitter.emit("capability", CastingCapabilityState.UNSUPPORTED);
        devices = [];
        emitter.emit("devices", []);
        return Promise.resolve();
      }
      // Availability comes from media element events after attachMediaElement
      emitter.emit("availability", false);
      emitter.emit("capability", CastingCapabilityState.DISCONNECTED);
      return Promise.resolve();
    },
    getCapabilityState: capabilityState,
    isAvailable: function () {
      if (!browserSupported) return false;
      return available || isWireless();
    },
    isConnected: function () {
      return isWireless();
    },
    discoverDevices: function () {
      refreshDevices();
      return Promise.resolve(devices.slice());
    },
    getDevices: function () {
      return devices.slice();
    },
    connect: function () {
      if (!browserSupported) {
        var unsupported = new Error(env.reason);
        lastError = unsupported;
        emitter.emit("error", {
          code: "AIRPLAY_UNSUPPORTED_BROWSER",
          message: unsupported.message,
          recoverable: false,
        });
        return Promise.reject(unsupported);
      }
      if (!video || typeof video.webkitShowPlaybackTargetPicker !== "function") {
        var err = new Error(
          "AirPlay picker unavailable until a video element is attached (Safari / WebKit)."
        );
        lastError = err;
        emitter.emit("error", {
          code: "AIRPLAY_UNAVAILABLE",
          message: err.message,
          recoverable: true,
        });
        return Promise.reject(err);
      }
      setState(CastingConnectionState.CONNECTING);
      emitter.emit("session", { type: "starting" });
      try {
        video.webkitShowPlaybackTargetPicker();
        // Connection confirmed asynchronously via wireless flag
        startSync();
        return Promise.resolve();
      } catch (e) {
        setState(CastingConnectionState.ERROR);
        lastError = e;
        emitter.emit("error", {
          code: "AIRPLAY_PICKER_FAILED",
          message: e.message || "AirPlay picker failed",
          recoverable: true,
        });
        setState(CastingConnectionState.DISCONNECTED);
        return Promise.reject(e);
      }
    },
    disconnect: function () {
      // AirPlay disconnect is user-driven via system UI; we mirror local state
      stopSync();
      setState(CastingConnectionState.DISCONNECTED);
      emitter.emit("session", { type: "ended", reason: "user" });
      refreshDevices();
      return Promise.resolve();
    },
    loadMedia: function () {
      // AirPlay uses the same media element source already loaded locally
      return Promise.resolve();
    },
    play: function () {
      if (!video) return Promise.resolve();
      var p = video.play();
      return p && p.then ? p : Promise.resolve();
    },
    pause: function () {
      if (video) video.pause();
      return Promise.resolve();
    },
    seek: function (time) {
      if (video && isFinite(time)) video.currentTime = time;
      return Promise.resolve();
    },
    stop: function () {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      return this.disconnect();
    },
    setVolume: function (level) {
      if (video) video.volume = Math.max(0, Math.min(1, level));
      return Promise.resolve();
    },
    setMuted: function (muted) {
      if (video) video.muted = !!muted;
      return Promise.resolve();
    },
    synchronizeState: function () {
      return Promise.resolve(getRemoteState());
    },
    getRemoteState: getRemoteState,
    getConnectionState: function () {
      return isWireless() ? CastingConnectionState.CONNECTED : state;
    },
    getLastError: function () {
      return lastError;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      stopSync();
      if (video) {
        try {
          video.removeEventListener(
            "webkitplaybacktargetavailabilitychanged",
            onAvailability
          );
        } catch (e) {
          /* ignore */
        }
      }
      video = null;
      emitter.removeAllListeners();
    },
  };
}

export default createAirPlayProvider;
